import React, { useState } from "react"
import * as Icons from "lucide-react"
import emailApi from "../services/emailApi"

/**
 * Mailbox connection settings.
 *
 * Only the essentials are collected here — email address, username and
 * password. The mail server details (Exchange/EWS + SMTP server, port, TLS)
 * are managed on the backend, so users don't have to enter them. The same
 * username/password is used for both receiving (EWS) and sending (SMTP).
 */
export default function EmailAccountSettings({
  account,
  isAdditional,
  onClose,
  onSaved,
  onLoggedOut,
  showToast,
}) {
  const [form, setForm] = useState({
    email_address: account?.email_address || "",
    smtp_username:
      account?.smtp_username ||
      account?.imap_username ||
      account?.email_address ||
      "",
    smtp_password: "",
  })
  const [saving, setSaving] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    const username = (form.smtp_username || form.email_address).trim()
    const payload = {
      email_address: form.email_address.trim(),
      display_name: account?.display_name || form.email_address.trim(),
      protocol: "ews",
      // one login used for both incoming (EWS) and outgoing (SMTP)
      smtp_username: username,
      imap_username: username,
      is_active: true,
      // server / port / EWS server are filled in on the backend
    }
    if (form.smtp_password) {
      payload.smtp_password = form.smtp_password
      payload.imap_password = form.smtp_password
    }

    try {
      let res
      if (account?.account_id) {
        res = await emailApi.updateAccount(account.account_id, payload)
      } else {
        res = await emailApi.createAccount(payload)
      }
      try {
        sessionStorage.removeItem("em-mailbox-loggedout")
      } catch {}
      showToast?.("Mailbox settings saved. Syncing…", "success")
      onSaved?.(res.data)
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to save mailbox settings."
      showToast?.(msg, "error")
    } finally {
      setSaving(false)
    }
  }

  const confirmLogout = async () => {
    if (!account?.account_id) return
    setLoggingOut(true)
    try {
      await emailApi.disconnectAccount(account.account_id)
      try {
        sessionStorage.setItem("em-mailbox-loggedout", "1")
      } catch {}
      showToast?.("Logged out of the mailbox.", "success")
      onLoggedOut?.(account.account_id)
    } catch (err) {
      showToast?.("Could not log out of the mailbox.", "error")
      setLoggingOut(false)
      setShowLogoutConfirm(false)
    }
  }

  return (
    <div className="em-search-overlay" onClick={onClose}>
      <div
        className="em-cal-modal em-acct-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="em-cal-modal-header">
          <h3>
            {account?.account_id
              ? "Mailbox settings"
              : isAdditional
              ? "Add another mailbox"
              : "Connect your mailbox"}
          </h3>
          <button className="em-search-close" onClick={onClose}>
            <Icons.X size={18} />
          </button>
        </div>

        <div className="em-cal-modal-body em-acct-body">
          <div className="em-acct-hint">
            <Icons.Info size={14} />
            <span>
              Enter <b>your email address</b> (e.g. <b>you@company.com</b>) —
              not a server. The mail server is set up automatically. Username is
              usually the same as your email.
            </span>
          </div>

          <div className="em-acct-grid">
            <label className="em-acct-field em-acct-col2">
              <span>Email address</span>
              <input
                value={form.email_address}
                onChange={e => {
                  const v = e.target.value
                  setForm(prev => ({
                    ...prev,
                    email_address: v,
                    // keep username in sync until the user edits it themselves
                    smtp_username:
                      !prev.smtp_username ||
                      prev.smtp_username === prev.email_address
                        ? v
                        : prev.smtp_username,
                  }))
                }}
                placeholder="you@company.com"
              />
            </label>
            <label className="em-acct-field em-acct-col2">
              <span>Username</span>
              <input
                value={form.smtp_username}
                onChange={e => set("smtp_username", e.target.value)}
                placeholder="you@company.com or DOMAIN\\user"
              />
            </label>
            <label className="em-acct-field em-acct-col2">
              <span>Password</span>
              <div className="em-acct-password-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.smtp_password}
                  onChange={e => set("smtp_password", e.target.value)}
                  placeholder={
                    account
                      ? "Leave blank to keep saved"
                      : "Your mailbox password"
                  }
                />
                <button
                  type="button"
                  className="em-acct-password-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  title={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <Icons.EyeOff size={16} />
                  ) : (
                    <Icons.Eye size={16} />
                  )}
                </button>
              </div>
            </label>
          </div>

          {account?.account_id && (
            <div className="em-acct-logout-row">
              <button
                className="em-acct-logout-btn"
                onClick={() => setShowLogoutConfirm(true)}
                disabled={loggingOut || saving}
              >
                <Icons.LogOut size={14} /> Log out of this mailbox
              </button>
            </div>
          )}

          {/*
            Managed on the backend / hidden per request:
              - Display name
              - Incoming mail (Exchange EWS) info banner + EWS server
              - SMTP server, Port, Use TLS
          */}
        </div>

        <div className="em-cal-modal-footer">
          <div className="em-cal-modal-footer-center">
            <button
              className="em-search-clear-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="em-compose-send-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save & Sync"}
            </button>
          </div>
        </div>

        {showLogoutConfirm && (
          <div
            className="em-confirm-overlay"
            onClick={() => !loggingOut && setShowLogoutConfirm(false)}
          >
            <div
              className="em-confirm-modal"
              onClick={e => e.stopPropagation()}
            >
              <div className="em-confirm-head">
                <Icons.LogOut size={16} /> Log out of this mailbox?
              </div>
              <div className="em-confirm-body">
                Your synced mail will be removed here. You can log in again
                anytime.
              </div>
              <div className="em-confirm-actions">
                <button
                  className="em-search-clear-btn"
                  onClick={() => setShowLogoutConfirm(false)}
                  disabled={loggingOut}
                >
                  Cancel
                </button>
                <button
                  className="em-confirm-danger"
                  onClick={confirmLogout}
                  disabled={loggingOut}
                >
                  {loggingOut ? "Logging out…" : "Log out"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
